/**
 * 价格趋势页面
 * 验证需求：6.1, 6.2, 6.3, 6.4, 6.5
 */
import { useState, useMemo } from 'react';
import { Space, Select, DatePicker, Radio, Row, Col, Statistic, Button } from 'antd';
import { RiseOutlined, FallOutlined, DownloadOutlined, CalendarOutlined } from '@ant-design/icons';
import { Dayjs } from 'dayjs';
import PriceTrendChart from '../components/PriceTrendChart';
import { LoadingIndicator, ErrorAlert, ExportDialog } from '../components';
import { useV2O5Prices, useFevPrices } from '../hooks/usePriceData';
import { aggregatePriceData, calculatePriceChange } from '../utils/priceUtils';
import { getDefaultDateRange, formatDateForApi, type TimeGranularity } from '../utils/dateUtils';
import CommonCard from '../components/CommonCard';
import './Prices.scss';

const { RangePicker } = DatePicker;

type MetalType = 'V2O5' | 'FeV';

export default function Prices() {
  const [metalType, setMetalType] = useState<MetalType>('V2O5');
  const [granularity, setGranularity] = useState<TimeGranularity>('day');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(getDefaultDateRange('day'));
  const [exportDialogVisible, setExportDialogVisible] = useState(false);

  // 构建查询参数
  const queryParams = useMemo(() => ({
    start_date: formatDateForApi(dateRange[0]),
    end_date: formatDateForApi(dateRange[1]),
  }), [dateRange]);

  // 获取数据
  const { data: v2o5Data, isLoading: v2o5Loading, error: v2o5Error, refetch: refetchV2O5 } = useV2O5Prices(
    metalType === 'V2O5' ? queryParams : undefined
  );

  const { data: fevData, isLoading: fevLoading, error: fevError, refetch: refetchFev } = useFevPrices(
    metalType === 'FeV' ? queryParams : undefined
  );

  // 选择当前数据
  const currentData = metalType === 'V2O5' ? v2o5Data : fevData;
  const isLoading = metalType === 'V2O5' ? v2o5Loading : fevLoading;
  const error = metalType === 'V2O5' ? v2o5Error : fevError;
  const refetch = metalType === 'V2O5' ? refetchV2O5 : refetchFev;

  // 聚合数据
  const aggregatedData = useMemo(() => {
    if (!currentData) return [];
    return aggregatePriceData(currentData, granularity);
  }, [currentData, granularity]);

  // 计算统计数据
  const statistics = useMemo(() => {
    if (!aggregatedData || aggregatedData.length === 0) {
      return {
        latest: 0,
        change: 0,
        max: 0,
        min: 0,
        avg: 0,
      };
    }

    const latest = aggregatedData[aggregatedData.length - 1];
    const previous = aggregatedData.length > 1 ? aggregatedData[aggregatedData.length - 2] : latest;
    const prices = aggregatedData.map((d) => d.price_cny);

    return {
      latest: latest.price_cny,
      change: calculatePriceChange(latest.price_cny, previous.price_cny),
      max: Math.max(...prices),
      min: Math.min(...prices),
      avg: prices.reduce((sum, p) => sum + p, 0) / prices.length,
    };
  }, [aggregatedData]);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const handleGranularityChange = (value: TimeGranularity) => {
    setGranularity(value);
    // 根据粒度调整默认日期范围
    setDateRange(getDefaultDateRange(value));
  };

  // 价格数据可导出字段
  const priceExportFields = [
    { value: 'date', label: '日期' },
    { value: 'metal_type', label: '金属类型' },
    { value: 'price_cny', label: '价格（人民币）' },
    { value: 'price_usd', label: '价格（美元）' },
    { value: 'volume', label: '交易量' },
    { value: 'data_source', label: '数据来源' },
  ];

  return (
    <div className="prices-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1f1f1f' }}>价格趋势分析</h2>
          <p style={{ margin: '4px 0 0', color: '#8c8c8c' }}>实时监控五氧化二钒与钒铁市场价格波动</p>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => setExportDialogVisible(true)}
          style={{ height: 40, padding: '0 24px', borderRadius: 6 }}
        >
          导出数据
        </Button>
      </div>

      {/* 筛选器 */}
      <CommonCard className="filter-section" style={{ marginBottom: 24 }}>
        <Space wrap size="large" align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space size="middle">
            <span style={{ fontWeight: 500 }}>金属类型：</span>
            <Select
              value={metalType}
              onChange={setMetalType}
              style={{ width: 180 }}
              size="large"
              options={[
                { label: '五氧化二钒 (V2O5)', value: 'V2O5' },
                { label: '钒铁 (FeV)', value: 'FeV' },
              ]}
            />
          </Space>

          <Space size="middle">
            <span style={{ fontWeight: 500 }}>时间粒度：</span>
            <Radio.Group value={granularity} onChange={(e) => handleGranularityChange(e.target.value)} size="large" buttonStyle="solid">
              <Radio.Button value="day">日</Radio.Button>
              <Radio.Button value="week">周</Radio.Button>
              <Radio.Button value="month">月</Radio.Button>
              <Radio.Button value="quarter">季度</Radio.Button>
              <Radio.Button value="year">年</Radio.Button>
            </Radio.Group>
          </Space>

          <Space size="middle">
            <span style={{ fontWeight: 500 }}>时间范围：</span>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              allowClear={false}
              size="large"
            />
          </Space>
        </Space>
      </CommonCard>

      {/* 统计卡片 */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <CommonCard style={{ marginBottom: 0, textAlign: 'center', height: '100%' }}>
            <Statistic
              title="最新价格"
              value={statistics.latest}
              precision={2}
              valueStyle={{ color: statistics.change >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}
              suffix={<span style={{ fontSize: 14, color: '#999' }}>元/吨</span>}
            />
            <div style={{ marginTop: 8, fontSize: 13, color: statistics.change >= 0 ? '#52c41a' : '#ff4d4f', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              {statistics.change >= 0 ? <RiseOutlined /> : <FallOutlined />}
              <span>{statistics.change >= 0 ? '上涨' : '下跌'} {Math.abs(statistics.change).toFixed(2)}%</span>
            </div>
          </CommonCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <CommonCard style={{ marginBottom: 0, textAlign: 'center', height: '100%' }}>
            <Statistic
              title="最高价格"
              value={statistics.max}
              precision={2}
              valueStyle={{ color: '#1f1f1f', fontWeight: 600 }}
              suffix={<span style={{ fontSize: 14, color: '#999' }}>元/吨</span>}
            />
          </CommonCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <CommonCard style={{ marginBottom: 0, textAlign: 'center', height: '100%' }}>
            <Statistic
              title="最低价格"
              value={statistics.min}
              precision={2}
              valueStyle={{ color: '#1f1f1f', fontWeight: 600 }}
              suffix={<span style={{ fontSize: 14, color: '#999' }}>元/吨</span>}
            />
          </CommonCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <CommonCard style={{ marginBottom: 0, textAlign: 'center', height: '100%' }}>
            <Statistic
              title="平均价格"
              value={statistics.avg}
              precision={2}
              valueStyle={{ color: '#1890ff', fontWeight: 600 }}
              suffix={<span style={{ fontSize: 14, color: '#999' }}>元/吨</span>}
            />
          </CommonCard>
        </Col>
      </Row>

      {/* 错误提示 */}
      {error && <ErrorAlert error={error} onRetry={refetch} />}

      {/* 加载状态 */}
      {isLoading && <LoadingIndicator loading={isLoading} tip="加载价格数据..." />}

      {/* 价格趋势图表 */}
      {!isLoading && !error && (
        <CommonCard title={`${metalType === 'V2O5' ? '五氧化二钒' : '钒铁'}价格趋势`} noPadding>
          <div style={{ padding: '24px' }}>
            <PriceTrendChart
              data={aggregatedData}
              title=""
              showDualAxis={true}
              granularity={granularity}
              height={500}
              theme="light" // Use light theme now as we have a clean white background card
            />
          </div>
        </CommonCard>
      )}

      {/* 导出对话框 */}
      <ExportDialog
        visible={exportDialogVisible}
        onClose={() => setExportDialogVisible(false)}
        dataType="price"
        availableFields={priceExportFields}
        currentFilters={{
          metal_type: metalType,
          start_date: formatDateForApi(dateRange[0]),
          end_date: formatDateForApi(dateRange[1]),
        }}
      />
    </div>
  );
}
